import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFlags } from "@/lib/flags";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StarRating } from "@/components/site/Stars";
import { formatIDR } from "@/lib/compareStore";
import { getSchoolUrl } from "@/lib/slug";
import WishlistButton from "@/components/site/WishlistButton";
import FoundingParentBadge from "@/components/site/FoundingParentBadge";
import { Camera, Heart, BadgeCheck, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { promptLogin, promptRegister } from "@/lib/authRedirect";

export default function Profile() {
  const { user, logout, checkAuth } = useAuth();
  const { school_reviews_enabled: reviewsOn, standalone_forum_enabled: forumOn } = useFlags();
  const nav = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/wishlist").then((r) => setWishlist(r.data.schools || [])),
      reviewsOn ? api.get("/reviews/mine").then((r) => setMyReviews(r.data || [])) : Promise.resolve(),
    ]).finally(() => setLoading(false));
  }, [user, reviewsOn]);

  const onPickAvatar = () => fileInputRef.current?.click();
  const onAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-picked
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error("Format tidak didukung. Gunakan JPEG, PNG, atau WebP.");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Ukuran foto maks 3 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await checkAuth(); // refresh AuthContext with the new picture URL
      toast.success("Foto profil berhasil diperbarui");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengunggah foto profil");
    } finally {
      setUploading(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto p-10 text-center" data-testid="profile-empty">
        <p className="text-[#4B5563]">Silakan masuk untuk melihat profil Anda.</p>
        <Button onClick={() => promptLogin(nav)} className="mt-4 rounded-full bg-[#1E3A8A] text-white">Masuk</Button>
      </div>
    );
  }

  return (
    <div data-testid="profile-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-center gap-5">
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-[#F3F1ED] border border-[#EAE7E0] flex items-center justify-center text-3xl text-[#1E3A8A] font-serif-display overflow-hidden">
            {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" data-testid="profile-avatar-img" /> : <span data-testid="profile-avatar-initial">{user.name?.[0]?.toUpperCase()}</span>}
          </div>
          <button
            type="button"
            onClick={onPickAvatar}
            disabled={uploading}
            data-testid="profile-avatar-upload-btn"
            aria-label={user.picture ? "Ganti foto profil" : "Unggah foto profil"}
            className={"absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white flex items-center justify-center border-2 border-white shadow-md transition-colors " + (uploading ? "opacity-70 cursor-wait" : "")}
          >
            {uploading ? (
              <span className="inline-block w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" aria-hidden="true" />
            ) : (
              <Camera size={14} strokeWidth={2.25} />
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onAvatarChange}
            data-testid="profile-avatar-input"
            className="hidden"
          />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-[#4B5563]">{user.role === "admin" ? "Editor" : "Orang tua"}</div>
          <h1 className="font-serif-display text-3xl text-[#1F2937]">{user.name}</h1>
          <div className="text-sm text-[#4B5563]">{user.email}</div>
          {user.is_founding_parent && (
            <div className="mt-2" data-testid="profile-founding-parent">
              <FoundingParentBadge size="sm" />
            </div>
          )}
        </div>
      </div>

      {/* Wishlist section */}
      <section className="mt-10">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#4B5563] inline-flex items-center gap-2">
              <Heart size={12} /> Wishlist saya
            </div>
            <h2 className="font-serif-display text-2xl text-[#1F2937] mt-1">Sekolah yang saya ikuti</h2>
            <p className="text-sm text-[#4B5563]">Kami kirim notifikasi tiap ada review atau ringkasan AI baru.</p>
          </div>
          <Link to="/sekolah" className="text-sm text-[#1E3A8A] hover:text-[#E07A5F]" data-testid="wishlist-browse">Jelajahi sekolah →</Link>
        </div>
        {loading && <div className="text-[#4B5563] text-sm">Memuat…</div>}
        {!loading && wishlist.length === 0 && (
          <div data-testid="wishlist-empty" className="bg-white border border-dashed border-[#EAE7E0] rounded-2xl p-10 text-center text-[#4B5563] text-sm">
            Belum ada sekolah favorit. Klik ikon hati pada kartu sekolah untuk menyimpan ke wishlist.
          </div>
        )}
        {wishlist.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wishlist-grid">
            {wishlist.map((s, i) => (
              <div key={s.school_id} data-testid={`wishlist-card-${i}`} className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden flex flex-col">
                <Link to={getSchoolUrl(s)} className="block">
                  <div className="aspect-[4/3] bg-[#F3F1ED] overflow-hidden">
                    {s.image_url && <img src={s.image_url} alt="" className="w-full h-full object-cover" />}
                  </div>
                </Link>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="text-xs uppercase tracking-widest text-[#4B5563]">{s.city}</div>
                  <Link to={getSchoolUrl(s)} className="font-serif-display text-base text-[#1F2937] mt-1 leading-tight hover:text-[#1E3A8A]">
                    {s.name}
                  </Link>
                  <div className="text-xs text-[#1E3A8A] font-medium mt-1">{formatIDR(s.tuition_monthly)}</div>
                  <div className="flex items-center justify-between mt-auto pt-3">
                    {reviewsOn ? <StarRating value={s.avg_rating} /> : <span />}
                    <WishlistButton schoolId={s.school_id} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="mt-10 grid sm:grid-cols-3 gap-4">
        <a href="/sekolah/usulkan" className="bg-white border border-[#EAE7E0] rounded-2xl p-5 hover:-translate-y-1 transition-transform duration-300" data-testid="profile-submit-school">
          <div className="text-xs uppercase tracking-widest text-[#4B5563]">Kontribusi</div>
          <div className="font-serif-display text-lg text-[#1F2937] mt-1">Usulkan sekolah</div>
          <div className="text-sm text-[#4B5563] mt-1">Bantu keluarga lain menemukan yang cocok.</div>
        </a>
        {forumOn && <a href="/forum" className="bg-white border border-[#EAE7E0] rounded-2xl p-5 hover:-translate-y-1 transition-transform duration-300" data-testid="profile-go-forum">
          <div className="text-xs uppercase tracking-widest text-[#4B5563]">Komunitas</div>
          <div className="font-serif-display text-lg text-[#1F2937] mt-1">Mulai diskusi</div>
          <div className="text-sm text-[#4B5563] mt-1">Tanyakan apa saja pada orang tua lain.</div>
        </a>}
        <a href="/compare" className="bg-white border border-[#EAE7E0] rounded-2xl p-5 hover:-translate-y-1 transition-transform duration-300" data-testid="profile-compare">
          <div className="text-xs uppercase tracking-widest text-[#4B5563]">Analisis</div>
          <div className="font-serif-display text-lg text-[#1F2937] mt-1">Bandingkan sekolah</div>
          <div className="text-sm text-[#4B5563] mt-1">Tabel sisi-per-sisi hingga 3 sekolah.</div>
        </a>
      </div>

      {/* Riwayat Review Saya */}
      {reviewsOn && <section className="mt-12" data-testid="my-reviews-section">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#4B5563] inline-flex items-center gap-2">
              <MessageSquare size={12} /> Riwayat review
            </div>
            <h2 className="font-serif-display text-2xl text-[#1F2937] mt-1">Review yang saya tulis</h2>
            <p className="text-sm text-[#4B5563]">Lihat rekam jejak review Anda dan status verifikasinya.</p>
          </div>
          {myReviews.length > 0 && (
            <div className="text-sm text-[#4B5563]" data-testid="my-reviews-summary">
              {myReviews.length} review · <span className="text-[#166534] font-semibold">{myReviews.filter(r => r.verified_parent).length} terverifikasi</span>
            </div>
          )}
        </div>
        {loading && <div className="text-sm text-[#4B5563]">Memuat…</div>}
        {!loading && myReviews.length === 0 && (
          <div data-testid="my-reviews-empty" className="bg-white border border-dashed border-[#EAE7E0] rounded-2xl p-8 text-center text-sm text-[#4B5563]">
            Anda belum menulis review. Buka <a href="/sekolah" className="text-[#1E3A8A] hover:text-[#E07A5F] underline">direktori</a> dan bagikan pengalaman Anda.
          </div>
        )}
        {myReviews.length > 0 && (
          <div className="space-y-3" data-testid="my-reviews-list">
            {myReviews.map((r, i) => (
              <a
                key={r.review_id}
                href={getSchoolUrl({ school_id: r.school_id, name: r.school_name, city: r.school_city })}
                data-testid={`my-review-${i}`}
                className="block bg-white border border-[#EAE7E0] rounded-2xl p-5 hover:border-[#1E3A8A] transition-colors"
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs uppercase tracking-widest text-[#4B5563]">
                    {r.school_name || "Sekolah"}{r.school_city ? ` · ${r.school_city}` : ""}
                    {" · "}{new Date(r.created_at).toLocaleDateString("id-ID")}
                  </div>
                  {r.verified_parent ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534] text-[10px] uppercase tracking-wider border border-[#BBF7D0]">
                      <BadgeCheck size={10} /> Terverifikasi{r.child_grade ? ` · ${r.child_grade}` : ""}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F3F1ED] text-[#4B5563] text-[10px] uppercase tracking-wider border border-[#EAE7E0]">
                      Belum diverifikasi
                    </span>
                  )}
                </div>
                <div className="mt-2 font-serif-display text-lg text-[#1F2937] leading-tight">{r.title}</div>
                <p className="mt-1 text-sm text-[#4B5563] line-clamp-2">{r.body}</p>
              </a>
            ))}
          </div>
        )}
      </section>}

      <div className="mt-10">
        <Button variant="outline" onClick={async () => { await logout(); nav("/"); }} data-testid="profile-logout" className="rounded-full border-[#EAE7E0]">
          Keluar
        </Button>
      </div>
    </div>
  );
}
